// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { useEffect, useRef, useState } from 'react';
import type { Theme } from './Sidebar.js';
import type { AnalyticsConsent } from '../preferences.js';
import { DetailsFields, type DetailsValue } from './DetailsFields.js';
import { messages, useMessages, type LanguageChoice } from '../i18n.js';

/**
 * The settings, as a real settings surface: a dialog with a rail of sections and one panel at a
 * time, the shape people already know from every product they use. Before this it was one long
 * scroll of five groups, which was honest but made the account's own details unreachable — they
 * could be stated once, at the door, and never changed again.
 *
 * Everything here is a choice this product actually offers. Nothing was invented to fill a panel,
 * and the two actions that can lose something are the parent's to confirm; this component asks.
 *
 * The dialog holds the keyboard while it is open: Escape closes it, Tab cycles inside it, the rail
 * is a tablist with arrow keys, and focus returns to whatever opened it.
 */
type SectionId = 'general' | 'voice' | 'profile' | 'privacy' | 'account' | 'about';

const THEME_VALUES: readonly Theme[] = [null, 'light', 'dark'];
const LANGUAGE_CHOICES: readonly LanguageChoice[] = ['auto', 'ar', 'en'];

/** One small mark per section, drawn from the same stroke set; decorative only. */
function SectionIcon({ id }: { readonly id: SectionId }): React.JSX.Element {
  const paths: Readonly<Record<SectionId, string>> = {
    general: 'M4 7h16M4 12h16M4 17h10',
    voice:
      'M12 4v10m0 0a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v4a3 3 0 0 0 3 3Zm-7 0a7 7 0 0 0 14 0M12 17v3',
    profile: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
    privacy: 'M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6l7-3Zm0 7v4',
    account: 'M4 7h16v10H4zM4 10h16',
    about: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13h.01M11 12h1v5h1',
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="settings-tab-icon">
      <path
        d={paths[id]}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const SECTION_IDS: readonly SectionId[] = [
  'general',
  'voice',
  'profile',
  'privacy',
  'account',
  'about',
];

const T = messages({
  ar: {
    themes: { auto: 'تلقائي', light: 'فاتح', dark: 'داكن' },
    languages: { auto: 'تلقائي', ar: 'العربية', en: 'English' },
    sections: {
      general: 'عام',
      voice: 'الصوت',
      profile: 'ملفك الشخصي',
      privacy: 'الخصوصية والبيانات',
      account: 'الحساب',
      about: 'عن بلال',
    },
    unknownInitial: '؟',
    saved: 'حُفظت بياناتك.',
    title: 'الإعدادات',
    close: 'إغلاق الإعدادات',
    railLabel: 'أقسام الإعدادات',
    yourAccount: 'حسابك',
    appearance: 'المظهر',
    appearanceHint: 'يتبع نظام جهازك ما لم تختر غير ذلك.',
    languageTitle: 'اللغة',
    languageHint:
      'لغة الواجهة. تلقائي يتبع لغة متصفحك. أما بلال فيردّ عليك باللغة التي تكتب بها، فاكتب بأي لغة منهما ويتبعك.',
    languageGroup: 'لغة الواجهة',
    autoSend: 'أرسل ما قلته فور الإفلات',
    autoSendHint:
      'عند الإيقاف، يظهر النص في الصندوق لتراجعه قبل الإرسال. وفي الحالتين يبقى النص غير المؤكّد للمراجعة.',
    voiceNote:
      'اضغط مطوّلًا على الميكروفون لتتحدّث، وأفلت لترسل؛ أو اضغط ضغطة واحدة للتسجيل حتى تضغط ثانية. اسحب للأعلى أثناء الضغط لإلغاء التسجيل دون إرساله.',
    profileNote: 'بهذا يخاطبك بلال، وعليه تُبنى إحصاءات مجمّعة بلا أسماء. يمكنك تغييرها متى شئت.',
    saving: 'يحفظ…',
    saveDetails: 'حفظ البيانات',
    analytics: 'مشاركة بيانات الاستخدام لتحسين بلال',
    analyticsHint:
      'أسماء الأحداث وعدّادات فقط. لا يُرسل نص محادثاتك ولا ما يتذكّره بلال عنك أبدًا.',
    memoryTitle: 'ما يتذكّره بلال عنك',
    memoryHint: 'اعرض كل ما هو محفوظ، وأكّد أو احذف كل بند على حدة.',
    openMemory: 'افتح الذاكرة',
    forgetTitle: 'حذف ما يتذكّره بلال',
    forgetHint: 'المحادثات نفسها تبقى. لا يمكن التراجع.',
    forget: 'حذف الذاكرة',
    eraseTitle: 'حذف كل المحادثات',
    eraseHint: (count: number) =>
      `${String(count)} محادثة تُحذف من حسابك، ومعها ما تذكّره بلال منها، ولن تستطيع استعادتها. قد يراجعها فريق بلال مدة تصل إلى 30 يومًا ثم تُمحى نهائيًا.`,
    erase: 'حذف المحادثات',
    privacyNoteBefore: 'تفاصيل ما نحتفظ به ومن يستطيع قراءته في ',
    privacyLink: 'سياسة الخصوصية',
    emailTitle: 'البريد الإلكتروني',
    emailHint: 'لا يمكن تغييره في هذه النسخة.',
    signOutTitle: 'الخروج من هذا الجهاز',
    signOutHint: 'تنتهي الجلسة هنا؛ حسابك يبقى كما هو.',
    signOut: 'تسجيل الخروج',
    deleteTitle: 'حذف الحساب نهائيًا',
    deleteHint: 'تُمحى محادثاتك وذاكرتك وبيانات دخولك، وتنتهي كل جلساتك. فورًا، ولا يمكن التراجع.',
    beta: 'نسخة تجريبية',
    aboutWhat:
      'بلال رفيق للتفكير في مشاكل الحياة اليومية واختيار خطوة واحدة صغيرة، للبالغين وبالعربية.',
    aboutNot:
      'ليس معالجًا نفسيًا وليس مفتيًا؛ لا يشخّص ولا يعالج ولا يصدر فتوى، وليس خدمة طوارئ. إن كنت في خطر الآن فاطلب مساعدة إنسان قريب منك أو جهة الطوارئ في بلدك.',
    aboutSources:
      'أي نص ديني يُعرض في بطاقة منفصلة، يُجلب من مزوّد معتمد ويُتحقَّق من بصمته قبل عرضه؛ بلال لا يكتبه ولا يعيد صياغته.',
    terms: 'شروط الاستخدام',
    privacy: 'سياسة الخصوصية',
  },
  en: {
    themes: { auto: 'Automatic', light: 'Light', dark: 'Dark' },
    languages: { auto: 'Automatic', ar: 'العربية', en: 'English' },
    sections: {
      general: 'General',
      voice: 'Voice',
      profile: 'Your profile',
      privacy: 'Privacy and data',
      account: 'Account',
      about: 'About Bilal',
    },
    unknownInitial: '?',
    saved: 'Your details are saved.',
    title: 'Settings',
    close: 'Close the settings',
    railLabel: 'Settings sections',
    yourAccount: 'Your account',
    appearance: 'Appearance',
    appearanceHint: 'Follows your device unless you choose otherwise.',
    languageTitle: 'Language',
    languageHint:
      'The language of the interface. Automatic follows your browser. Bilal himself replies in the language you write in, so write in either and he follows you.',
    languageGroup: 'Interface language',
    autoSend: 'Send what you said as soon as you release',
    autoSendHint:
      'When off, the words land in the box for you to check before sending. Either way, anything uncertain is left for review.',
    voiceNote:
      'Press and hold the microphone to speak, and release to send; or tap once to record until you tap again. Slide up while holding to discard the recording.',
    profileNote:
      'This is how Bilal addresses you, and what the aggregate statistics are built from — never names. You can change it whenever you like.',
    saving: 'Saving…',
    saveDetails: 'Save these details',
    analytics: 'Share usage data to improve Bilal',
    analyticsHint:
      'Event names and counts only. The text of your conversations, and what Bilal remembers about you, are never sent.',
    memoryTitle: 'What Bilal remembers about you',
    memoryHint: 'See everything that is saved, and confirm or delete each item.',
    openMemory: 'Open the memory',
    forgetTitle: 'Delete what Bilal remembers',
    forgetHint: 'The conversations themselves stay. This cannot be undone.',
    forget: 'Delete the memory',
    eraseTitle: 'Delete every conversation',
    eraseHint: (count: number) =>
      `${String(count)} conversation(s) leave your account, with everything Bilal remembered from them, and you cannot get them back. The Bilal team may review them for up to 30 days; then they are erased for good.`,
    erase: 'Delete the conversations',
    privacyNoteBefore: 'What we keep and who can read it is set out in the ',
    privacyLink: 'privacy policy',
    emailTitle: 'Email',
    emailHint: 'It cannot be changed in this build.',
    signOutTitle: 'Sign out of this device',
    signOutHint: 'The session ends here; your account stays as it is.',
    signOut: 'Sign out',
    deleteTitle: 'Delete the account permanently',
    deleteHint:
      'Your conversations, your memory and your sign-in details are erased, and every session ends. Immediately, and it cannot be undone.',
    beta: 'Beta',
    aboutWhat:
      'Bilal is a companion for thinking through everyday problems and choosing one small step. For adults, in Arabic.',
    aboutNot:
      'Not a therapist and not a mufti; it does not diagnose, treat or issue religious rulings, and it is not an emergency service. If you are in danger now, reach a person near you or your local emergency services.',
    aboutSources:
      'Any religious text appears in its own card, fetched from an approved provider and checked against its fingerprint before it is shown; Bilal does not write it or rephrase it.',
    terms: 'Terms of use',
    privacy: 'Privacy policy',
  },
});

function Row({
  title,
  hint,
  children,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="setting-row">
      <div className="setting-label">
        <span className="setting-title">{title}</span>
        {hint !== undefined && <span className="setting-hint">{hint}</span>}
      </div>
      <div className="setting-control">{children}</div>
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly hint: string;
  readonly checked: boolean;
  readonly onChange: (on: boolean) => void;
}): React.JSX.Element {
  return (
    <label className="switch-row">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      <span>
        <span className="switch-title">{label}</span>
        <span className="switch-hint">{hint}</span>
      </span>
    </label>
  );
}

export interface SettingsProps {
  readonly theme: Theme;
  readonly onTheme: (theme: Theme) => void;
  readonly voiceAutoSend: boolean;
  readonly onVoiceAutoSend: (on: boolean) => void;
  readonly analyticsConsent: AnalyticsConsent | null;
  readonly onAnalyticsConsent: (consent: AnalyticsConsent) => void;
  /** What the person chose, not what it resolved to: `auto` must stay visible as a choice. */
  readonly languageChoice: LanguageChoice;
  readonly onLanguage: (choice: LanguageChoice) => void;
  /** The account's stated details, editable here for the first time (ADR-034). */
  readonly details: DetailsValue;
  readonly onDetailsChange: (next: DetailsValue) => void;
  /** Resolves true when the server accepted them; the panel says so either way. */
  readonly onSaveProfile: () => Promise<boolean>;
  readonly savingProfile: boolean;
  /** Shown so the person knows which account this is; null when the account has no email. */
  readonly accountEmail: string | null;
  readonly onOpenMemory: () => void;
  readonly onForgetProfile: () => void;
  readonly onEraseConversations: () => void;
  readonly conversationCount: number;
  readonly onSignOut: () => void;
  /** Absent when the server offers no account deletion; the control is then not drawn at all. */
  readonly onDeleteAccount?: (() => void) | undefined;
  /**
   * The application's current failure, shown inside the dialog. It used to render only behind the
   * scrim, so a refused save looked like nothing happening at all (founder, 2026-09-13).
   */
  readonly notice?: string | null | undefined;
  readonly onClose: () => void;
}

/** The account's own initial, so the rail says whose settings these are. */
function initialOf(name: string, email: string | null, fallback: string): string {
  const source = name.trim().length > 0 ? name.trim() : (email ?? '');
  return source.slice(0, 1).toUpperCase() || fallback;
}

export function Settings(props: SettingsProps): React.JSX.Element {
  const t = useMessages(T);
  const [section, setSection] = useState<SectionId>('general');
  const [profileNotice, setProfileNotice] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement | null>(null);
  const opener = useRef<Element | null>(null);

  // The dialog takes the keyboard and gives it back: focus moves in on open, returns to whatever
  // opened it on close, and Tab cannot walk out into the conversation behind.
  useEffect(() => {
    opener.current = document.activeElement;
    dialog.current?.focus();
    const returnTo = opener.current;
    return () => {
      if (returnTo instanceof HTMLElement) returnTo.focus();
    };
  }, []);

  const focusables = (): HTMLElement[] =>
    [
      ...(dialog.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      ) ?? []),
    ].filter((node) => node.offsetParent !== null || node === document.activeElement);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      props.onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = focusables();
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (first === undefined || last === undefined) return;
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  };

  /** The rail is a tablist, so the arrow keys move between sections as they do everywhere else. */
  const onRailKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const order = SECTION_IDS;
    const at = order.indexOf(section);
    const step =
      event.key === 'ArrowDown' || event.key === 'ArrowLeft'
        ? 1
        : event.key === 'ArrowUp' || event.key === 'ArrowRight'
          ? -1
          : event.key === 'Home'
            ? -at
            : event.key === 'End'
              ? order.length - 1 - at
              : 0;
    if (step === 0 && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const next = order[(at + step + order.length) % order.length];
    if (next === undefined) return;
    setSection(next);
    // The tab that becomes current takes the focus with it, as a tablist must.
    globalThis.setTimeout(() => {
      dialog.current?.querySelector<HTMLElement>(`#settings-tab-${next}`)?.focus();
    }, 0);
  };

  const saveProfile = (): void => {
    setProfileNotice(null);
    void props.onSaveProfile().then((saved) => {
      setProfileNotice(saved ? t.saved : null);
    });
  };

  return (
    <div className="settings-scrim">
      <div
        className="settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
        tabIndex={-1}
        ref={dialog}
        onKeyDown={onKeyDown}
      >
        <header className="settings-head">
          <h2>{t.title}</h2>
          <button
            type="button"
            className="icon-button"
            aria-label={t.close}
            onClick={props.onClose}
          >
            ✕
          </button>
        </header>

        <div className="settings-body">
          <div
            className="settings-rail"
            role="tablist"
            aria-label={t.railLabel}
            aria-orientation="vertical"
            onKeyDown={onRailKeyDown}
          >
            <div className="settings-whose" aria-hidden="true">
              <span className="settings-avatar">
                {initialOf(props.details.displayName, props.accountEmail, t.unknownInitial)}
              </span>
              <span className="settings-whose-text">
                <span className="settings-whose-name">
                  {props.details.displayName.trim().length > 0
                    ? props.details.displayName
                    : t.yourAccount}
                </span>
                <span className="settings-whose-mail" dir="ltr">
                  {props.accountEmail ?? ''}
                </span>
              </span>
            </div>
            {SECTION_IDS.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`settings-tab-${id}`}
                aria-selected={section === id}
                aria-controls={`settings-panel-${id}`}
                tabIndex={section === id ? 0 : -1}
                onClick={() => {
                  setSection(id);
                }}
              >
                <SectionIcon id={id} />
                <span>{t.sections[id]}</span>
              </button>
            ))}
          </div>

          <div
            className="settings-panel"
            role="tabpanel"
            id={`settings-panel-${section}`}
            aria-labelledby={`settings-tab-${section}`}
            tabIndex={0}
          >
            {typeof props.notice === 'string' && props.notice.length > 0 && (
              <p className="settings-failure" role="alert">
                {props.notice}
              </p>
            )}
            {section === 'general' && (
              <>
                <Row title={t.appearance} hint={t.appearanceHint}>
                  <div className="segmented" role="group" aria-label={t.appearance}>
                    {THEME_VALUES.map((value) => (
                      <button
                        key={value ?? 'auto'}
                        type="button"
                        aria-pressed={props.theme === value}
                        onClick={() => {
                          props.onTheme(value);
                        }}
                      >
                        {t.themes[value ?? 'auto']}
                      </button>
                    ))}
                  </div>
                </Row>
                <Row title={t.languageTitle} hint={t.languageHint}>
                  <div className="segmented" role="group" aria-label={t.languageGroup}>
                    {LANGUAGE_CHOICES.map((choice) => (
                      <button
                        key={choice}
                        type="button"
                        lang={choice === 'auto' ? undefined : choice}
                        aria-pressed={props.languageChoice === choice}
                        onClick={() => {
                          props.onLanguage(choice);
                        }}
                      >
                        {t.languages[choice]}
                      </button>
                    ))}
                  </div>
                </Row>
              </>
            )}

            {section === 'voice' && (
              <>
                <Toggle
                  label={t.autoSend}
                  hint={t.autoSendHint}
                  checked={props.voiceAutoSend}
                  onChange={props.onVoiceAutoSend}
                />
                <p className="settings-note">{t.voiceNote}</p>
              </>
            )}

            {section === 'profile' && (
              <>
                <p className="settings-note">{t.profileNote}</p>
                <div className="settings-form">
                  <DetailsFields
                    idPrefix="settings"
                    value={props.details}
                    onChange={props.onDetailsChange}
                  />
                </div>
                <div className="settings-actions">
                  <button
                    type="button"
                    className="button-primary"
                    disabled={props.savingProfile}
                    onClick={saveProfile}
                  >
                    {props.savingProfile ? t.saving : t.saveDetails}
                  </button>
                </div>
                {profileNotice !== null && (
                  <p className="settings-saved" role="status">
                    {profileNotice}
                  </p>
                )}
              </>
            )}

            {section === 'privacy' && (
              <>
                <Toggle
                  label={t.analytics}
                  hint={t.analyticsHint}
                  checked={props.analyticsConsent === 'granted'}
                  onChange={(on) => {
                    props.onAnalyticsConsent(on ? 'granted' : 'denied');
                  }}
                />
                <Row title={t.memoryTitle} hint={t.memoryHint}>
                  <button type="button" onClick={props.onOpenMemory}>
                    {t.openMemory}
                  </button>
                </Row>
                <Row title={t.forgetTitle} hint={t.forgetHint}>
                  <button type="button" className="danger" onClick={props.onForgetProfile}>
                    {t.forget}
                  </button>
                </Row>
                <Row title={t.eraseTitle} hint={t.eraseHint(props.conversationCount)}>
                  <button type="button" className="danger" onClick={props.onEraseConversations}>
                    {t.erase}
                  </button>
                </Row>
                <p className="settings-note">
                  {t.privacyNoteBefore}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    {t.privacyLink}
                  </a>
                  .
                </p>
              </>
            )}

            {section === 'account' && (
              <>
                <Row title={t.emailTitle} hint={t.emailHint}>
                  <span className="settings-account" dir="ltr">
                    {props.accountEmail ?? '—'}
                  </span>
                </Row>
                <Row title={t.signOutTitle} hint={t.signOutHint}>
                  <button type="button" onClick={props.onSignOut}>
                    {t.signOut}
                  </button>
                </Row>
                {props.onDeleteAccount !== undefined && (
                  <div className="settings-danger">
                    <Row title={t.deleteTitle} hint={t.deleteHint}>
                      <button type="button" className="danger" onClick={props.onDeleteAccount}>
                        {t.deleteTitle}
                      </button>
                    </Row>
                  </div>
                )}
              </>
            )}

            {section === 'about' && (
              <>
                <p className="beta-chip">{t.beta}</p>
                <p className="settings-note">{t.aboutWhat}</p>
                <p className="settings-note">{t.aboutNot}</p>
                <p className="settings-note">{t.aboutSources}</p>
                <div className="settings-actions">
                  <a href="/terms" target="_blank" rel="noreferrer">
                    {t.terms}
                  </a>
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    {t.privacy}
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
